import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import { capitalizeFirst } from './utils';
import './UserMenu.css';

function UserMenu({ username, onLogout, onManageAccounts, onManageTags, onSwitchUser }) {
  const [isOpen, setIsOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [viewingAs, setViewingAs] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      fetchAvailableUsers();
      fetchCurrentViewingAs();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    // Check viewing_as on mount
    fetchCurrentViewingAs();
  }, []);

  const fetchAvailableUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await axios.get(`${API_URL}/portfolio/user-access-grants/available_users/`);
      setAvailableUsers(response.data || []);
    } catch (err) {
      console.error('Error fetching available users:', err);
      setAvailableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchCurrentViewingAs = async () => {
    try {
      const response = await axios.get(`${API_URL}/portfolio/current-viewing-as/`);
      setViewingAs(response.data.viewing_as);
    } catch (err) {
      console.error('Error fetching current viewing_as:', err);
      setViewingAs(null);
    }
  };

  const handleSwitchUser = async (targetUsername) => {
    try {
      setIsOpen(false);
      const response = await axios.post(`${API_URL}/portfolio/switch-user/`, {
        username: targetUsername
      });
      setViewingAs(response.data.viewing_as);
      // Refresh the page or notify parent to refresh data
      if (onSwitchUser) {
        onSwitchUser(response.data.viewing_as);
      } else {
        window.location.reload();
      }
    } catch (err) {
      console.error('Error switching user:', err);
      alert('Error switching user: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleLogout = () => {
    setIsOpen(false);
    onLogout();
  };

  const handleManageAccounts = () => {
    setIsOpen(false);
    onManageAccounts();
  };

  const handleManageTags = () => {
    setIsOpen(false);
    onManageTags();
  };

  return (
    <div className="user-menu-container" ref={menuRef}>
      <button 
        className="user-menu-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="User menu"
      >
        <svg 
          className="user-icon" 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
        >
          <circle cx="12" cy="8" r="5"/>
          <path d="M20 21a8 8 0 0 0-16 0"/>
        </svg>
        <span className="user-name">{capitalizeFirst(username)}</span>
        <svg 
          className={`dropdown-arrow ${isOpen ? 'open' : ''}`}
          width="12" 
          height="12" 
          viewBox="0 0 12 12" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
        >
          <path d="M2 4l4 4 4-4"/>
        </svg>
      </button>
      
      {isOpen && (
        <div className="user-menu-dropdown">
          <button 
            className="user-menu-item"
            onClick={handleManageAccounts}
          >
            Settings
          </button>
          <button 
            className="user-menu-item"
            onClick={handleManageTags}
          >
            Manage Tags
          </button>
          
          {/* Switch User Section */}
          {availableUsers.length > 1 && (
            <>
              <div className="user-menu-divider"></div>
              <div className="user-menu-section-header">Switch User</div>
              {loadingUsers ? (
                <div className="user-menu-item disabled">Loading...</div>
              ) : (
                availableUsers.map((user) => (
                  <button
                    key={user.id}
                    className={`user-menu-item ${viewingAs && viewingAs.username === user.username ? 'active' : ''}`}
                    onClick={() => handleSwitchUser(user.username)}
                  >
                    {capitalizeFirst(user.username)}
                    {viewingAs && viewingAs.username === user.username && ' ✓'}
                  </button>
                ))
              )}
            </>
          )}
          
          <div className="user-menu-divider"></div>
          <button 
            className="user-menu-item logout"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      )}
    </div>
  );
}

export default UserMenu;

